import {
  CreateDateColumn,
  UpdateDateColumn,
  DeleteDateColumn,
  Entity,
  Index,
  Column,
  RelationId,
  ManyToOne,
  OneToMany,
  VersionColumn,
  PrimaryGeneratedColumn
} from 'typeorm'
import { ObjectType, Field, Int, ID, registerEnumType } from 'type-graphql'

import { Domain } from '@things-factory/shell'
import { User } from '@things-factory/auth-base'
import { config } from '@things-factory/env'
import { ScalarObject } from '@things-factory/shell'

import { KpiScores } from './kpi-grade.types'

const ORMCONFIG = config.get('ormconfig', {})
const DATABASE_TYPE = ORMCONFIG.type

export enum KpiStatus {
  DRAFT = 'DRAFT',
  RELEASE = 'RELEASE',
  ARCHIVED = 'ARCHIVED'
}

export enum KpiVizType {
  CARD = 'CARD',
  GAUGE = 'GAUGE',
  PROGRESS = 'PROGRESS',
  BAR = 'BAR',
  LINE = 'LINE',
  PIE = 'PIE',
  DONUT = 'DONUT',
  RADAR = 'RADAR',
  BULLET = 'BULLET',
  THERMOMETER = 'THERMOMETER',
  SPEEDOMETER = 'SPEEDOMETER',
  ICON = 'ICON',
  BADGE = 'BADGE',
  TEXT = 'TEXT',
  TABLE = 'TABLE'
}

export enum KpiPeriodType {
  DAY = 'DAY',
  WEEK = 'WEEK',
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
  YEAR = 'YEAR',
  RANGE = 'RANGE',
  ALLTIME = 'ALLTIME'
}

/**
 * KPI scoreType — value → score 변환 방식
 *
 * 미설정(null)이면 score 변환이 정의되지 않은 상태.
 *
 * DIRECT  — value = score, 별도 변환 없음.
 *           value 자체가 최종 성과 점수로 사용됨.
 *           예: Y1~Y6(영역별 성과), Z(전체스코어) — formula로 계산된 value가 곧 score.
 *           예: X14(일정성과 수준 평가) — 감리자가 입력한 1~5점이 곧 score.
 *
 * FORMULA — scoreFormula 수식으로 value → score 변환.
 *           KPI 엔티티의 scoreFormula 필드에 정의된 수식을 적용.
 *           예: "1 - value / 0.82", "if(value >= 90, 100, 70)"
 *
 * LOOKUP  — grade table(1D 배열)로 raw value → 0~1 score 변환.
 *           KPI 엔티티의 grades 필드에 {minValue, maxValue, score} 배열로 정의.
 *           value가 속하는 구간의 score를 반환.
 *           예: X11(연면적 대비 공사기간), X33(검측 불합격률), X34(품질 SL-PA)
 *
 * CUSTOM  — 2D 룩업 등 애플리케이션에서 정의하는 특수 변환.
 *           grades에 다차원 lookup 구조가 저장됨.
 *           예: X13(일정 이탈 수준) — 공정률(%) × 편차율(%) → 1~5점
 */
export enum KpiScoreType {
  DIRECT = 'DIRECT',
  FORMULA = 'FORMULA',
  LOOKUP = 'LOOKUP',
  CUSTOM = 'CUSTOM'
}

/**
 * KPI valueType — value 획득 방식
 *
 * KPI의 value(측정값/입력값)를 어떤 방식으로 얻는지를 정의한다.
 * scoreType(value→score 변환)과는 독립적인 개념.
 *
 * MEASURED   — 외부 시스템(API, DB 연계)에서 자동 수집한 정량 측정값.
 *              연속 실수값이며, 단위와 범위가 KPI마다 다름.
 *              예: X11(연면적 대비 공사기간), X33(검측 불합격률), X41(재해율), X61(투입인력 생산성)
 *              보통 scoreType=LOOKUP과 함께 사용하여 grade table로 score 변환.
 *
 * ASSESSED   — 감리자/평가자가 직접 입력한 정성 평가.
 *              배점 기준(1~5점 척도)에 따라 수동 입력됨.
 *              예: X14(일정성과 수준 평가), X23(비용성과), X35(품질성과), X44(안전성과)
 *              보통 scoreType=DIRECT와 함께 사용 (value가 곧 score).
 *
 * CALCULATED — 산식(formula 필드)으로 하위 KPI들의 가중합 등을 자동 계산.
 *              KPI 엔티티의 formula 필드에 정의된 수식을 기반으로 서버에서 연산.
 *              예: Y1(일정성과) = X11*0.3 + X12*0.4 + X13/5*0.15 + X14/5*0.15
 *              예: Z(전체스코어) = Y1~Y6의 가중 평균
 *              보통 scoreType=DIRECT와 함께 사용 (계산 결과가 곧 score).
 *
 * COMPOSITE  — 다차원 입력의 복합 결과.
 *              단일 raw value가 아닌 복수의 입력(공정률, 편차율 등)을 조합하여 산출.
 *              예: X13(일정 이탈 수준) — 공정률(%) × 공기편차(%) 2D 룩업 → 1~5점
 *              보통 scoreType=CUSTOM과 함께 사용.
 */
export enum KpiValueType {
  MEASURED = 'MEASURED',
  ASSESSED = 'ASSESSED',
  CALCULATED = 'CALCULATED',
  COMPOSITE = 'COMPOSITE'
}

registerEnumType(KpiStatus, {
  name: 'KpiStatus',
  description: 'State enumeration of a KPI (DRAFT, RELEASED, ARCHIVED)'
})

registerEnumType(KpiVizType, {
  name: 'KpiVizType',
  description: 'Visualization type for KPI display (CARD, GAUGE, PROGRESS, etc.)'
})

registerEnumType(KpiPeriodType, {
  name: 'KpiPeriodType',
  description: 'Aggregation period type for KPI (DAY, WEEK, MONTH, QUARTER, RANGE, ALLTIME)'
})

registerEnumType(KpiScoreType, {
  name: 'KpiScoreType',
  description:
    'value → score 변환 방식. DIRECT: value=score(변환 없음), FORMULA: scoreFormula 수식 적용, LOOKUP: grade table(1D) 매핑, CUSTOM: 2D 룩업 등 특수 변환.'
})

registerEnumType(KpiValueType, {
  name: 'KpiValueType',
  description:
    'value 획득 방식. MEASURED: 외부 시스템 수집 측정값, ASSESSED: 감리자 직접 평가(1~5), CALCULATED: formula 자동 계산, COMPOSITE: 다차원 복합 입력 결과.'
})

@Entity()
@Index('ix_kpi_0', (kpi: Kpi) => [kpi.domain, kpi.name], {
  where: '"deleted_at" IS NULL',
  unique: true
})
@Index('ix_kpi_hierarchy', (kpi: Kpi) => [kpi.domain, kpi.parent], {
  where: '"deleted_at" IS NULL'
})
@ObjectType({
  description:
    'KPI entity. Represents a key performance indicator with calculation formula, target, category, and other attributes.'
})
export class Kpi {
  @PrimaryGeneratedColumn('uuid')
  @Field(type => ID, { description: 'Unique identifier for this KPI.' })
  readonly id: string

  @VersionColumn()
  @Field({
    nullable: true,
    description:
      'Version number of the KPI. Increments on each modification. When the KPI is released, a snapshot is saved in kpi-history and the status becomes RELEASED. Editing after release increases the version and sets status to DRAFT.'
  })
  version?: number = 1

  @ManyToOne(type => Domain)
  @Field({ nullable: true, description: 'Domain (tenant) to which this KPI belongs.' })
  domain?: Domain

  @RelationId((kpi: Kpi) => kpi.domain)
  @Field({ nullable: true, description: 'ID of the domain (tenant) for this KPI.' })
  domainId?: string

  @Column()
  @Field({ nullable: true, description: 'Name of the KPI.' })
  name?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Detailed description of the KPI.' })
  description?: string

  @ManyToOne(() => Kpi, { nullable: true })
  @Field(type => Kpi, { nullable: true, description: 'Parent KPI in hierarchical structure.' })
  parent?: Kpi

  @RelationId((kpi: Kpi) => kpi.parent)
  @Field({ nullable: true, description: 'ID of the parent KPI.' })
  parentId?: string

  @OneToMany(() => Kpi, kpi => kpi.parent)
  @Field(type => [Kpi], { nullable: true, description: 'Child KPIs in hierarchical structure.' })
  children?: Kpi[]

  @Column({ nullable: false, default: true })
  @Field({ nullable: true, description: 'Indicates whether this KPI is a leaf node (has actual measured values).' })
  isLeaf?: boolean

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({
    nullable: true,
    description:
      'Calculation formula for the KPI. Expressed as a string using metric codes and operators, e.g., "defect_count / total_count * 100".'
  })
  formula?: string

  @Column({ nullable: false, default: false })
  @Field({ nullable: true, description: 'Indicates whether this KPI is active and usable.' })
  active?: boolean

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Current state of the KPI (DRAFT, RELEASED, ARCHIVED).' })
  state?: KpiStatus

  @Column({ nullable: true })
  @Field(type => KpiVizType, {
    nullable: true,
    description: 'Visualization type for this KPI (e.g., CARD, GAUGE, PROGRESS, BAR, LINE, etc.).'
  })
  vizType?: KpiVizType

  @Column({ type: 'json', nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Visualization options and metadata for this KPI, such as color, icon, thresholds, unit, decimal places, min/max values, etc.'
  })
  vizMeta?: any

  @Column({ nullable: true })
  @Field({
    nullable: true,
    description: 'Cron schedule string for periodic KPI value aggregation (e.g., "0 0 * * *" for daily).'
  })
  schedule?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Schedule ID for the KPI (used for scheduler registration).' })
  scheduleId?: string

  @Column({ nullable: true })
  @Field({ nullable: true, description: 'Timezone for the KPI schedule.' })
  timezone?: string

  @Column({ default: 'DAY' })
  @Field(type => KpiPeriodType, { description: 'Aggregation period type for this KPI.' })
  periodType: KpiPeriodType

  @Column({ type: 'float', nullable: true, default: 1 })
  @Field({ nullable: true, description: 'Weight for aggregation in parent category.' })
  weight?: number

  @Column({ nullable: true })
  @Field(type => KpiScoreType, {
    nullable: true,
    description:
      'value → score 변환 방식. NONE: 미설정, DIRECT: value=score(변환 없음), FORMULA: scoreFormula 수식, LOOKUP: grade table(1D), CUSTOM: 2D 룩업 등 특수 변환.'
  })
  scoreType?: KpiScoreType

  @Column({ nullable: true })
  @Field(type => KpiValueType, {
    nullable: true,
    description:
      'value 획득 방식. MEASURED: 외부 시스템 수집 측정값, ASSESSED: 감리자 직접 평가(1~5), CALCULATED: formula 자동 계산, COMPOSITE: 다차원 룩업 결과.'
  })
  valueType?: KpiValueType

  @Column({ type: 'simple-json', nullable: true })
  @Field(type => ScalarObject, {
    nullable: true,
    description:
      'Performance index lookup table for complex transformations. @deprecated 향후 제거 예정. performanceFormula 사용 권장.'
  })
  grades?: KpiScores

  @Column({
    nullable: true,
    type:
      DATABASE_TYPE == 'mysql' || DATABASE_TYPE == 'mariadb'
        ? 'longtext'
        : DATABASE_TYPE == 'oracle'
          ? 'clob'
          : DATABASE_TYPE == 'mssql'
            ? 'nvarchar'
            : 'varchar',
    length: DATABASE_TYPE == 'mssql' ? 'MAX' : undefined
  })
  @Field({
    nullable: true,
    description:
      'Score calculation formula for this KPI. Converts KPI value to performance score (0-100). For complex mappings, use grades lookup table instead. Example: "if(value >= 90, 100, if(value >= 80, 85, 70))" or "value * 0.8 + 20"'
  })
  scoreFormula?: string

  @CreateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI was created.' })
  createdAt?: Date

  @UpdateDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI was last updated.' })
  updatedAt?: Date

  @DeleteDateColumn()
  @Field({ nullable: true, description: 'Timestamp when this KPI was deleted (soft delete).' })
  deletedAt?: Date

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who created this KPI.' })
  creator?: User

  @RelationId((kpi: Kpi) => kpi.creator)
  @Field({ nullable: true, description: 'ID of the user who created this KPI.' })
  creatorId?: string

  @ManyToOne(type => User, { nullable: true })
  @Field(type => User, { nullable: true, description: 'User who last updated this KPI.' })
  updater?: User

  @RelationId((kpi: Kpi) => kpi.updater)
  @Field({ nullable: true, description: 'ID of the user who last updated this KPI.' })
  updaterId?: string

  @Field(type => String, { nullable: true, description: 'Thumbnail image or file path for this KPI.' })
  thumbnail?: string
}
